import React, { useState, useLayoutEffect, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { TicketDetail as TicketDetailType, PiiIgnoreEntry } from '../types';
import { CategoryBadge, PanelSection, Spinner, SkeletonBlock, styleRedactionTokens, escapeHtml, escapeHtmlText, isHtml, CheckIcon, CrossIcon, SpreadsheetRowIcon } from './shared';
import { SecondaryAxisPills } from './SecondaryAxisPills';
import { theme } from '../theme';
import { CONFIDENCE_LABELS, STATUS_LABELS, PIPELINE_STAGE_LABELS, PERSONAL_DATA_LABELS, normalizeKey, SECONDARY_AXIS_MAX_VISIBLE } from '../constants';
import { smarttix } from '../services/api';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import { useTicketManager } from '../contexts/TicketManagerContext';
import KlaerfallEditor from './KlaerfallEditor';
import NoDraftCard from './NoDraftCard';
import ClerkInstructionPanel from './ClerkInstructionPanel';
import TriagePanel from './TriagePanel';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { useKlaerfallDraft } from '../hooks/useKlaerfallDraft';
import { isNoDraft } from '../services/replyEngineMeta';
import { resolveAttachmentOcrRows, partitionAttachmentRows, countAttachments, inlineSummaryLabel, OCR_STATE_LABELS, OCR_DECIDER_LABELS, formatBytes, ocrReasonLabel } from '../services/attachmentOcrStatus';

// Why (#5): suggestion HTML carries <span data-edit-start="1"></span>...<span data-edit-end="1"></span>
// markers (migration 039) that bracket the editable Antwortvorschlag region. Split the HTML at those
// markers so the FE can make ONLY the inner span contentEditable (chrome stays immutable).
// Returns null when markers are missing (legacy pre-039 suggestions) — caller hides edit affordance.
const splitEditableRegion = (html: string): { prefix: string; inner: string; suffix: string } | null => {
  const startRe = /<span\s+data-edit-start="1"\s*><\/span>/i;
  const endRe = /<span\s+data-edit-end="1"\s*><\/span>/i;
  const sm = html.match(startRe);
  const em = html.match(endRe);
  if (!sm || !em || sm.index === undefined || em.index === undefined) return null;
  const innerStart = sm.index + sm[0].length;
  const innerEnd = em.index;
  if (innerEnd <= innerStart) return null;
  return {
    prefix: html.slice(0, innerStart),
    inner: html.slice(innerStart, innerEnd),
    suffix: html.slice(innerEnd),
  };
};

interface TicketDetailProps {
  ticket: TicketDetailType;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  onClearError: () => void;
  onApprove: () => void;
  onReject: () => void;
  onDownload: () => void;
  onGoToUploads?: () => void;
  onReprocess?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  piiIgnoreList?: PiiIgnoreEntry[];
  onPiiIgnore?: (fieldType: string, value: string) => Promise<void>;
  // #5 edit-before-approve — invoked after a successful PATCH so parent reloads ticket.
  onSuggestionEdited?: () => void;
  // Copy-approval: fired when Kopieren is pressed in Offen — copying records an indirect approval.
  onCopyApprove?: () => void;
  // Demo-only: gates the "Simulierte Systemdaten" fill button in the scaffold editor.
  simulateSystemData?: boolean;
  // Tenant setting (operator_config UI_ATTACHMENTS_COUNT_INLINE via /config): whether inline
  // signature-image attachments count toward Anhänge and list as rows. ABSENT ⇒ true — every host
  // threads this from the same App-level /config read (see attachmentsCountInline in App.tsx), and
  // an omitted prop must reproduce the behaviour that shipped before this rule existed, never a
  // silent flip to the new posture.
  attachmentsCountInline?: boolean;
  // Tenant setting (operator_config UI_SHOW_SOURCE_CHANNEL via /config): whether the Eingangsweg row
  // is shown at all. ABSENT ⇒ HIDDEN — every host passes it explicitly; an omitted prop must never
  // reveal the row, because "this host forgot to thread it" and "the tenant asked for it" would then
  // be the same state.
  showSourceChannel?: boolean;
  // Raised when an action declines to complete for a reason the clerk must READ and act on — currently
  // a refused form-row copy. The page owns toasts; this component reports and does not render one, which
  // also keeps an appearing message from shifting the detail layout.
  onInfo?: (message: string) => void;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });


const TicketDetailComponent: React.FC<TicketDetailProps> = ({
  ticket, loading, actionLoading, error, onClearError, onApprove, onReject, onDownload, onGoToUploads, onReprocess, onArchive, onRestore,
  piiIgnoreList, onPiiIgnore, onSuggestionEdited, onCopyApprove, simulateSystemData, showSourceChannel, onInfo,
  attachmentsCountInline = true,
}) => {
  // During maintenance the server 423s all writes; disable the per-ticket write
  // actions so the banner's "Aktionen pausiert" is literal (no optimistic-remove
  // → 423 → reappear flicker). Read-only actions (download, navigation) stay live.
  const maintenance = useMaintenanceMode();
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const [kiExpanded, setKiExpanded] = useState(true);
  const [emailExpanded, setEmailExpanded] = useState(false);
  // Anhänge: starts expanded like KI-Vorschlag (kiExpanded) and, like both neighbours, keeps the
  // user's toggle across tickets — no per-uuid reset exists for the sibling sections and none is
  // added here. Always MOUNTED: the count in the title carries the "keine Anhänge" fact.
  const [anhaengeExpanded, setAnhaengeExpanded] = useState(true);
  // ticket?.-safe like every other derivation above the `if (!ticket) return null` guard below:
  // this runs on the null-ticket render the host pages (PopoutShell/Antworten/Archiv) pass through
  // while a selection is clearing, before that guard short-circuits.
  const anhaengeRows = resolveAttachmentOcrRows(ticket?.attachments, ticket?.ocrDecisions);
  const { documents: anhaengeDocuments, inline: anhaengeInline } = partitionAttachmentRows(anhaengeRows);
  // ON keeps anhaengeRows completely untouched (today's join order, byte-identical) rather than
  // re-concatenating documents+inline — re-concatenating would silently reorder any row whose
  // partition bucket differs from its position in the raw list (e.g. a document whose OCR decision
  // alone carries reason:'inline', per isInlineAttachment's documented fallback precedence), which
  // is exactly the drift ON must never show. OFF is documents-only, so "documents first" is met
  // trivially — inline images never appear as rows in that posture at all.
  const anhaengeVisibleRows = attachmentsCountInline ? anhaengeRows : anhaengeDocuments;
  const anhaengeCount = countAttachments(anhaengeRows, attachmentsCountInline);
  const extractionSummary = ticket?.suggestion?.extractionSummary ?? null;
  const [btnFade, setBtnFade] = useState<'in' | 'out'>('in');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [rowFeedback, setRowFeedback] = useState(false);
  const [rowError, setRowError] = useState(false);
  const btnKeyRef = useRef('');
  const [dismissedPii, setDismissedPii] = useState<Set<string>>(new Set());
  const prevTicketUuid = useRef('');

  // ─── #5 edit-before-approve state ────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editableRef = useRef<HTMLDivElement>(null);

  // Reset edit mode when ticket changes
  useEffect(() => {
    setEditMode(false);
    setEditSaving(false);
    setEditError(null);
  }, [ticket?.uuid]);

  // VNB scaffold hook — unconditional (rules-of-hooks). Non-scaffold tickets pass [] so
  // allConfirmed=true, openCount=0 — the !scaffold guard in canApprove short-circuits anyway.
  const scaffold = ticket?.suggestion?.scaffold ?? null;
  // Key the draft by the SUGGESTION attempt (ai_suggestions.id), not the ticket uuid — so a
  // reanalysis (a new suggestion under the same uuid) resets the fill-in to a clean slate
  // instead of bleeding the prior attempt's drafts into the fresh scaffold.
  const draftKey = ticket?.suggestion?.id != null ? `sug-${ticket.suggestion.id}` : (ticket?.uuid ?? '');
  const draft = useKlaerfallDraft(
    draftKey,
    scaffold?.segments ?? [],
    async (text, scaffoldSegments) => {
      setEditSaving(true); setEditError(null);
      // suggestionId (fix-review round 1): ticket.suggestion.id IS the draftKey's numeric source
      // (see draftKey above) — read straight from the closure rather than re-derived, so the
      // server merges into the EXACT row this fill was confirmed against.
      const res = await tm.editSuggestion(ticket.uuid, text, scaffoldSegments, ticket.suggestion?.id);
      setEditSaving(false);
      if (res.success) { onSuggestionEdited?.(); return true; }
      setEditError(res.error || 'Fehler beim Speichern'); return false;
    },
  );

  // Simulierte Systemdaten (demo): ask the BE for deterministic values for the empty system slots,
  // then fill them through the SAME draft path a rep uses (fillSystemSlots → editSuggestion). Returns
  // the filled segment indices so the editor can flash them. Never overwrites mail-filled slots (the
  // BE only resolves placeholder slots; we match returned slotIds back to their segment index).
  const handleSimulate = useCallback(async (): Promise<number[]> => {
    if (!ticket) return [];
    const segs = scaffold?.segments ?? [];
    const res = await smarttix.simulateSystemData(ticket.uuid);
    if (!res.success) { setEditError(res.error || 'Fehler beim Abrufen der Systemdaten'); return []; }
    const fills = (res.filledSlots ?? [])
      .map(fs => {
        const index = segs.findIndex(s => s.kind === 'slot' && s.slotId === fs.slotId);
        return index >= 0 ? { index, value: fs.value } : null;
      })
      .filter((x): x is { index: number; value: string } => x !== null);
    // Deliberate ~0.55s beat so the fill reads as a system lookup returning results, not an instant
    // form auto-fill — the "generic → concrete because we queried the system" gesture.
    await new Promise(r => setTimeout(r, 550));
    await draft.fillSystemSlots(fills);
    return fills.map(f => f.index);
  }, [ticket, scaffold, draft]);

  // All hooks before early returns (React rules-of-hooks)
  // Why: 2-slot model — key changes when slot content changes, triggering crossfade
  const btnKey = ticket ? `${ticket.status}-${ticket.hasDraft}` : '';
  useLayoutEffect(() => {
    if (btnKeyRef.current && btnKey !== btnKeyRef.current) {
      setBtnFade('out');
      const t = setTimeout(() => { btnKeyRef.current = btnKey; setBtnFade('in'); }, 150);
      return () => clearTimeout(t);
    }
    btnKeyRef.current = btnKey;
  }, [btnKey]);
  useLayoutEffect(() => {
    if (ticket && ticket.uuid !== prevTicketUuid.current) {
      prevTicketUuid.current = ticket.uuid;
      setDismissedPii(new Set());
    }
  }, [ticket]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={32} label="Lade Details..." />
      </div>
    );
  }

  if (!ticket) return null;

  // D3: a deliberate engine no-draft (empty suggestion + reply_engine envelope) blocks all three
  // draft actions — the Step-9 .eml blob exists but is EMPTY; approving/copying/downloading it is
  // the footgun the NoDraftCard explains. Single predicate, shared by render branch + guards.
  const noDraft = isNoDraft(ticket?.suggestion);
  // A triage suggestion (wim) has NO letter draft — its actions are the TriagePanel's assisted
  // confirms. isNoDraft cannot say so (it is replyEngine-scoped and returns false without one),
  // which is how a triage ticket rendered an enabled Genehmigen that "approved" an empty
  // non-draft and credited Abnahmequote (operator repro 2026-08-31). The letter-flow slot-1
  // button is suppressed for triage tickets instead.
  const isTriage = !!ticket?.suggestion?.triage;
  const noDraftTooltip = 'Kein Entwurf vorhanden — direkte Bearbeitung erforderlich';
  const canApprove = ticket.status === 'suggestion_ready' && (!scaffold || draft.allConfirmed) && !noDraft && !isTriage;
  // Demo-only: show the fill button only while there are still-empty system (placeholder) slots and
  // the ticket is open. Keys off draft state (not segment.status) so it hides once filled/approved.
  const canSimulate = !!simulateSystemData && ticket.status === 'suggestion_ready' && !!scaffold &&
    scaffold.segments.some((s, i) => s.kind === 'slot' && s.status === 'placeholder' && !draft.isConfirmed(i));
  const canReject = ticket.status === 'suggestion_ready';
  const canReprocess = ticket.status === 'pipeline_error' && !!onReprocess;
  const canArchive = ['approved', 'sent', 'pipeline_error'].includes(ticket.status) && !!onArchive;
  const canRestore = ticket.status === 'archived' && !!onRestore;
  const isPipelineError = ticket.status === 'pipeline_error';
  const stageLabel = ticket.pipelineStage ? (PIPELINE_STAGE_LABELS[ticket.pipelineStage] || ticket.pipelineStage) : undefined;
  return (
    <div style={{ fontFamily: theme.font.family, display: 'flex', flexDirection: 'column', height: '100%', animation: 'stx-fadeIn 0.3s ease' }}>
      {/* Action error banner */}
      {error && (
        <div style={{
          padding: theme.spacing.md, backgroundColor: '#fef2f2', color: theme.colors.error,
          borderRadius: theme.borderRadius.md, marginBottom: theme.spacing.md,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
        }}>
          {error}
          <button onClick={onClearError} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.error, fontSize: 14,
          }}>
            ✕
          </button>
        </div>
      )}

      {/* Pipeline error banner */}
      {isPipelineError && (
        <div style={{
          padding: theme.spacing.md, backgroundColor: '#fef2f2', color: theme.colors.error,
          borderRadius: theme.borderRadius.md, marginBottom: theme.spacing.md, fontSize: 13, lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Pipeline-Fehler{stageLabel ? ` bei ${stageLabel}` : ''}
            {ticket.retryCount != null && ticket.retryCount > 1 && (
              <span style={{ fontWeight: 400, marginLeft: 8 }}>({ticket.retryCount} Versuche)</span>
            )}
          </div>
          {ticket.errorMessage && (
            <div style={{ fontSize: 12, color: '#991b1b', fontFamily: 'monospace', wordBreak: 'break-word' }}>
              {ticket.errorMessage}
            </div>
          )}
        </div>
      )}

      {/* Header: subject + metadata */}
      <div style={{ marginBottom: theme.spacing.lg, borderBottom: `1px solid ${theme.colors.borderLight}`, paddingBottom: theme.spacing.md }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: theme.colors.navy }}>
          {ticket.subject || '(Kein Betreff)'}
        </h2>
        <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginTop: theme.spacing.xs, display: 'flex', flexWrap: 'wrap', gap: theme.spacing.sm }}>
          <span>{ticket.customerEmail}</span>
          <span style={{ color: theme.colors.borderLight }}>|</span>
          <span>{formatDate(ticket.receivedAt)}</span>
          {ticket.primaryAxis && (
            <>
              <span style={{ color: theme.colors.borderLight }}>|</span>
              <CategoryBadge category={ticket.primaryAxis} style={{ fontSize: 11, padding: '2px 8px' }} />
              {ticket.isForceOverride && (
                <span style={{
                  fontSize: 9, fontWeight: 600, color: theme.colors.navyMid,
                  backgroundColor: theme.colors.bgLight, border: `1px solid ${theme.colors.borderLight}`,
                  borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap',
                }}>erzwungen</span>
              )}
              {ticket.secondaryAxis && (
                <SecondaryAxisPills codes={ticket.secondaryAxis} maxVisible={SECONDARY_AXIS_MAX_VISIBLE.detail} variant="detail" />
              )}
              {(ticket.eskalationsstufe ?? 0) >= 1 && (() => {
                const n = Math.min(Math.max(ticket.eskalationsstufe!, 1), 3) as 1 | 2 | 3;
                const e = theme.colors.eskalation[`s${n}` as 's1' | 's2' | 's3'];
                return <span style={{ color: e.fg, background: e.bg, border: `1px solid ${e.border}`, borderRadius: 10, fontSize: 10, fontWeight: 600, padding: '1px 8px' }}>Stufe {n}</span>;
              })()}
              {ticket.classifierConfidence != null && (
                <span style={{ fontSize: 11, color: theme.colors.navyMuted }}>
                  {(ticket.classifierConfidence * 100).toFixed(0)}% {cm.categoryNounCompound}konfidenz
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content sections */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* AI Suggestion — show invalidation overlay during reanalysis */}
        {(ticket.suggestion || ['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status)) && (() => {
          const sugg = ticket.suggestion ?? null;
          const isVnbScaffold = !!sugg?.scaffold;
          const generating = ['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status);
          const suggDisplayedText = sugg ? (sugg.editedText ?? sugg.suggestionText) : '';
          const suggIsHtml = /<[a-z][\s\S]*>/i.test(suggDisplayedText);
          const suggSplit = suggIsHtml ? splitEditableRegion(suggDisplayedText) : null;
          const canEditSugg = !!suggSplit && ticket.status === 'suggestion_ready';
          const wasEditedSugg = !!sugg?.editedText;
          const kiHeaderAction = (sugg && !isVnbScaffold && !generating && !editMode) ? (
            <>
              {wasEditedSugg && (
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                  border: `1px solid ${theme.colors.borderLight}`,
                }}>bearbeitet</span>
              )}
              <button
                onClick={() => canEditSugg && setEditMode(true)}
                disabled={!canEditSugg}
                title={canEditSugg
                  ? (wasEditedSugg ? 'Bearbeitung anpassen' : 'KI-Vorschlag bearbeiten')
                  : (ticket.status !== 'suggestion_ready'
                      ? 'Bearbeitung nur vor Genehmigen möglich'
                      : 'Bearbeitung erfordert Neuverarbeitung mit neuem Prompt')}
                style={{
                  padding: '2px 10px', fontSize: 11, fontWeight: 500, fontFamily: theme.font.family,
                  color: canEditSugg ? theme.colors.navyMid : theme.colors.textSecondary,
                  backgroundColor: 'transparent',
                  border: `1px solid ${canEditSugg ? theme.colors.borderLight : theme.colors.bgLight}`,
                  borderRadius: theme.borderRadius.sm,
                  cursor: canEditSugg ? 'pointer' : 'not-allowed',
                  opacity: canEditSugg ? 1 : 0.5,
                }}
              >✎ Bearbeiten</button>
            </>
          ) : undefined;
          return (
          <PanelSection title="KI-Vorschlag" collapsible expanded={kiExpanded} onToggle={() => setKiExpanded(p => !p)} headerAction={kiHeaderAction}>
            <div style={{
              position: 'relative',
              // Why: when no prior suggestion exists yet (fresh Verarbeiten), the overlay needs a canvas
              // to paint on — otherwise the position:absolute scanlines collapse to 0 height and the
              // panel looks dead. ~140px ≈ one suggestion height.
              minHeight: !ticket.suggestion && ['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status) ? 140 : undefined,
            }}>
              {ticket.suggestion && (() => {
                // WIM triage ticket (data-driven: only wim rows carry a triage record) — the
                // panel IS the suggestion surface; no letter editor exists for the triage role.
                if (ticket.suggestion.triage) {
                  return (
                    <TriagePanel
                      uuid={ticket.uuid}
                      suggestionVersion={ticket.suggestion.id}
                      triage={ticket.suggestion.triage}
                      // Same immediate refetch every other mutation on this pane uses (see
                      // editSuggestion above) — without it the confirmed triage state only
                      // reached this window on the next background poll tick.
                      onActionDone={onSuggestionEdited}
                    />
                  );
                }

                // Why (#5): editedText wins over suggestionText for display once CS rep has saved a version.
                // The original suggestionText stays untouched in DB for stat-baseline + LLM-steering.
                const displayedText = ticket.suggestion.editedText ?? ticket.suggestion.suggestionText;
                const isHtmlSuggestion = /<[a-z][\s\S]*>/i.test(displayedText);
                const split = isHtmlSuggestion ? splitEditableRegion(displayedText) : null;
                const baseStyle: React.CSSProperties = {
                  padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.borderLight}`,
                  fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
                };

                // D3 deliberate no-draft → self-explaining state card instead of a blank
                // editor (engine no-drafts ship scaffold={segments:[]}, so this branch must
                // precede the scaffold one). Reads only the labels-only envelope.
                if (noDraft) {
                  return (
                    <NoDraftCard
                      replyEngine={ticket.suggestion!.replyEngine!}
                      reviewReasons={ticket.suggestion!.reviewReasons}
                      marktprozess={ticket.primaryAxis ?? ''}
                      eskalationsstufe={ticket.eskalationsstufe}
                      clerkInstruction={ticket.suggestion!.clerkInstruction}
                    />
                  );
                }

                // VNB scaffold present → deterministic fill-in Klärfall editor (replaces
                // the gMSB contentEditable path). gMSB suggestions have scaffold===null and
                // fall through to the split/SuggestionEditor + read-only render below.
                if (ticket.suggestion!.scaffold) {
                  // Historical engine override tickets carry the letter ONLY in suggestionText with
                  // empty segments (pre-fix envelope shape) — synthesize the single text segment the
                  // fixed engine now emits, so the Notfall copy always renders. noDraft (both empty)
                  // was already handled above.
                  const scaffoldSegments = ticket.suggestion!.scaffold.segments.length > 0
                    ? ticket.suggestion!.scaffold.segments
                    : [{ kind: 'text' as const, value: displayedText }];
                  return (
                    <>
                      {/* E3: every engine ticket gets Warum/Was-tun — incl. GRÜN letters
                          („prüfen und freigeben"); absent on pre-engine tickets. */}
                      {ticket.suggestion!.clerkInstruction && (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          <ClerkInstructionPanel instruction={ticket.suggestion!.clerkInstruction} />
                        </div>
                      )}
                      <KlaerfallEditor
                        segments={scaffoldSegments}
                        eskalationsstufe={ticket.eskalationsstufe}
                        marktprozess={ticket.primaryAxis ?? ''}
                        zone={ticket.suggestion!.replyEngine?.zone}
                        draft={draft}
                        simulateEnabled={canSimulate}
                        onSimulate={handleSimulate}
                      />
                      {editError && (
                        <div role="alert" style={{ marginTop: theme.spacing.xs, fontSize: 12, color: theme.colors.error }}>
                          {editError}
                        </div>
                      )}
                    </>
                  );
                }

                if (editMode && split) {
                  return <SuggestionEditor
                    split={split}
                    saving={editSaving}
                    error={editError}
                    editableRef={editableRef}
                    baseStyle={baseStyle}
                    onCancel={() => { setEditMode(false); setEditError(null); }}
                    onSave={async () => {
                      if (!editableRef.current) return;
                      setEditSaving(true); setEditError(null);
                      const newInner = editableRef.current.innerHTML;
                      const fullHtml = split.prefix + newInner + split.suffix;
                      const res = await tm.editSuggestion(ticket.uuid, fullHtml);
                      setEditSaving(false);
                      if (res.success) {
                        setEditMode(false);
                        onSuggestionEdited?.();
                      } else {
                        setEditError(res.error || 'Fehler beim Speichern');
                      }
                    }}
                  />;
                }

                return (
                  <>
                    {isHtmlSuggestion ? (
                      <div
                        style={baseStyle}
                        dangerouslySetInnerHTML={{ __html: styleRedactionTokens(DOMPurify.sanitize(displayedText)) }}
                      />
                    ) : (
                      <div
                        style={{ ...baseStyle, whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: styleRedactionTokens(displayedText) }}
                      />
                    )}
                  </>
                );
              })()}
              {/* Why: stale suggestion stays visible but overlaid with animated scanlines while new one generates */}
              {['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status) && (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: theme.borderRadius.md,
                  overflow: 'hidden',
                  animation: 'stx-fadeIn 0.3s ease',
                }}>
                  {/* Animated diagonal scanlines */}
                  <div data-stx-scanlines style={{
                    position: 'absolute', inset: -50,
                    backgroundImage: `repeating-linear-gradient(
                      -45deg,
                      transparent, transparent 8px,
                      rgba(0, 13, 102, 0.06) 8px, rgba(0, 13, 102, 0.06) 16px
                    )`,
                    backgroundSize: '22.6px 22.6px',
                    animation: 'stx-scanlines 1s linear infinite',
                  }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    backgroundColor: 'rgba(245, 246, 250, 0.75)',
                  }} />
                  <div style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                    fontSize: 13, fontWeight: 500, color: theme.colors.navyMid,
                  }}>
                    <Spinner size={18} />
                    {ticket.status === 'categorizing' && 'Wird kategorisiert...'}
                    {ticket.status === 'categorized' && 'Kategorisiert — Vorschlag wird vorbereitet...'}
                    {ticket.status === 'generating_suggestion' && 'Vorschlag wird generiert...'}
                    {ticket.status === 'reanalyzing' && 'Neuer Vorschlag wird generiert...'}
                  </div>
                </div>
              )}
            </div>
            {ticket.suggestion && !['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status) && (
              <div style={{ marginTop: theme.spacing.sm, fontSize: 11, color: theme.colors.navyMuted, display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                {ticket.suggestion.suggestionConfidence != null && <span>{CONFIDENCE_LABELS.suggestion}: {(ticket.suggestion.suggestionConfidence * 100).toFixed(0)}%</span>}
                {ticket.suggestion.processingTimeMs != null && <span>{ticket.suggestion.processingTimeMs}ms</span>}
                {(() => {
                  const wasEdited = !!ticket.suggestion!.editedText;
                  const needsAction = scaffold
                    ? draft.openCount > 0
                    : (ticket.suggestion!.requiresReview && !wasEdited);
                  return needsAction && ticket.status === 'suggestion_ready' ? (
                    <span style={{ color: theme.colors.navy, fontWeight: 500 }}>Review erforderlich</span>
                  ) : null;
                })()}
                {/* Form-Excel row copy (Task 12): gated on ticket.hasFormExtraction so it only
                    mounts on tickets where OCR recognized a form (the row itself is fetched
                    server-side on click — PII-minimal, not shipped on the detail DTO). Sits left
                    of Kopieren's marginLeft:auto — conditional mount/unmount does not reflow
                    Kopieren's position since flex marginLeft:auto anchors it to the row's right
                    edge regardless of preceding siblings. */}
                {ticket.hasFormExtraction && (
                  <button
                    /* Provenance on hover rather than as a new line: a mail can carry several
                       attachments, so which one the form was read from is the question a clerk
                       actually has. Rendered in the title because the field is nullable even when
                       hasFormExtraction is true (tickets predating the column carry no key) — a
                       conditional caption would shift this row for exactly those tickets. */
                    title={ticket.formSourceFileName
                      ? `Excel-Zeile kopieren — Formular aus: ${ticket.formSourceFileName}`
                      : 'Excel-Zeile kopieren'}
                    onClick={() => {
                      // Why: single .catch on the whole chain — copyFormRow (404/500/network) and
                      // clipboard.writeText (permission denial) both surface as the same transient
                      // "Fehler" state instead of an unhandled promise rejection.
                      // A REFUSAL IS NOT AN ERROR. The server declines to put a row with something to
                      // check on the clipboard, because the clipboard carries no notes and the
                      // destination sheet is terminal. Show the reason — which names the annotated
                      // download as the way forward — rather than a red tick the clerk cannot act on.
                      smarttix.copyFormRow(ticket.uuid)
                        .then((res) => {
                          if (res.blocked || !res.row) {
                            const why = res.reason
                              ?? 'Diese Zeile wird nicht kopiert. Bitte den Formular-Excel-Download verwenden.';
                            // Fallback is the transient error state rather than nothing: a refusal that
                            // produces no visible signal is indistinguishable from a copy that worked.
                            if (onInfo) onInfo(why); else { setRowError(true); setTimeout(() => setRowError(false), 2500); }
                            return;
                          }
                          return navigator.clipboard.writeText(res.row).then(() => {
                            setRowFeedback(true);
                            setTimeout(() => setRowFeedback(false), 1500);
                          });
                        })
                        .catch(() => {
                          setRowError(true);
                          setTimeout(() => setRowError(false), 1500);
                        });
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 10px', fontSize: 11, fontWeight: 500, fontFamily: theme.font.family,
                      color: rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.navyMid,
                      backgroundColor: 'transparent',
                      border: `1px solid ${rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >
                    <SpreadsheetRowIcon size={14} color={rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.navyMid} />
                    {rowError ? 'Fehler' : rowFeedback ? 'In Zwischenablage!' : 'Excel-Zeile'}
                  </button>
                )}
                {/* Copy-approval (#4): Kopieren is surfaced in Offen so copying the suggestion
                    records an indirect approval (statistic accuracy). In suggestion_ready it is
                    the primary "grab" affordance (filled navy); post-approval it stays a quiet
                    bordered re-copy that does NOT re-credit.
                    Gate: reuse the same slots-filled predicate as canApprove — Kopieren grants
                    copy-approved credit, so it must not credit an incomplete scaffold. */}
                {(() => {
                  const isOffen = ticket.status === 'suggestion_ready';
                  const prominent = isOffen && !copyFeedback && !noDraft;
                  // Same slots-filled check as canApprove (single source of truth: draft.allConfirmed).
                  const slotsAllFilled = !scaffold || draft.allConfirmed;
                  // noDraft blocks in EVERY status: copying an empty suggestion would grant
                  // copy-approval credit for nothing (corrupts the Abnahmequote).
                  const kopiereBlocked = (isOffen && !slotsAllFilled) || noDraft;
                  return (
                  <button
                    disabled={kopiereBlocked}
                    title={noDraft
                      ? noDraftTooltip
                      : kopiereBlocked ? `Noch ${draft.openCount} ${draft.openCount === 1 ? 'Feld' : 'Felder'} offen – bitte zuerst alle Felder ausfüllen` : undefined}
                    onClick={() => {
                      if (kopiereBlocked) return;
                      // Why: editedText wins over suggestionText — a clerk who edited the AI's draft
                      // and then clicks Kopieren must get the letter they wrote, not the model's
                      // original wording. Copying the unedited suggestionText here silently pastes
                      // the wrong text into Outlook (see displayedText above, same precedence).
                      // Strip HTML tags for clean clipboard text — suggestion may contain HTML markup.
                      const plain = (ticket.suggestion!.editedText ?? ticket.suggestion!.suggestionText).replace(/<[^>]*>/g, '');
                      navigator.clipboard.writeText(plain).then(() => {
                        setCopyFeedback(true);
                        setTimeout(() => setCopyFeedback(false), 1500);
                        // Indirect approval, Offen only. Skip the credit during maintenance (server 423s
                        // all writes) — the clipboard copy itself stays available so reps can still grab text.
                        if (ticket.status === 'suggestion_ready' && !maintenance) onCopyApprove?.();
                      });
                    }}
                    style={{
                      marginLeft: 'auto', padding: '2px 10px', fontSize: 11, fontWeight: prominent ? 600 : 500,
                      fontFamily: theme.font.family,
                      color: copyFeedback ? theme.colors.success : prominent ? theme.colors.white : theme.colors.navyMid,
                      backgroundColor: prominent ? theme.colors.navy : 'transparent',
                      border: `1px solid ${copyFeedback ? theme.colors.success : prominent ? theme.colors.navy : theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm,
                      cursor: kopiereBlocked ? 'not-allowed' : 'pointer',
                      opacity: kopiereBlocked ? 0.6 : 1,
                      transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
                    }}
                  >
                    {copyFeedback ? 'Kopiert!' : 'Kopieren'}
                  </button>
                  );
                })()}
              </div>
            )}
          </PanelSection>
          );
        })()}

        {/* Anhänge — the attachment facts the reply preamble used to carry: which files, whether OCR
            read them (and who decided), and the model's digest of what it read. Always mounted.
            Title stays the bare string 'Anhänge' while cm.configLoading: PanelSection's title prop
            is typed `string` (shared.tsx), so the inclusive count cannot be painted and then
            corrected once the tenant's counting rule settles — that would be a content shift,
            which the layout-stability rule forbids outright. */}
        <PanelSection title={cm.configLoading ? 'Anhänge' : `Anhänge (${anhaengeCount})`} collapsible expanded={anhaengeExpanded} onToggle={() => setAnhaengeExpanded(p => !p)}>
          <div data-testid="ticket-detail-anhaenge" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            {cm.configLoading ? (
              // Row COUNT is known before the flag is (attachments already loaded with the ticket) —
              // only the grouping/number depend on config. One shimmer per known row keeps the
              // section's settled height from the very first paint, never expanding when it resolves.
              anhaengeRows.map((row, i) => (
                <SkeletonBlock key={`${row.name}#${i}`} height={16} />
              ))
            ) : anhaengeVisibleRows.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.colors.textSecondary, padding: `${theme.spacing.xs}px 0` }}>
                {anhaengeInline.length > 0 ? 'Keine Dokumente beigefügt' : 'Keine Anhänge'}
              </div>
            ) : anhaengeVisibleRows.map((row, i) => {
              const decider = row.decidedBy ? OCR_DECIDER_LABELS[row.decidedBy] : null;
              const chipText = decider ? `${OCR_STATE_LABELS[row.state]} · ${decider}` : OCR_STATE_LABELS[row.state];
              const chipColor = row.state === 'read' ? theme.colors.success : row.state === 'not_read' ? theme.colors.navyMuted : theme.colors.textSecondary;
              const chipTitle = row.state === 'unknown'
                ? 'OCR: keine OCR-Entscheidung protokolliert'
                : row.reason ? `OCR: ${chipText} — ${ocrReasonLabel(row.reason)}` : `OCR: ${chipText}`;
              return (
                <div key={`${row.name}#${i}`} data-testid="anhang-row" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, padding: `${theme.spacing.xs}px 0` }}>
                  <span title={row.name} style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.navyMid, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
                  <span data-testid="anhang-size" style={{ fontSize: 11, color: theme.colors.textSecondary, flexShrink: 0, minWidth: 56, textAlign: 'right' }}>{formatBytes(row.size)}</span>
                  <span
                    data-testid="anhang-ocr-chip"
                    title={chipTitle}
                    // Fixed width (not minWidth): the longest face is 'nicht gelesen · Sachbearbeiter' —
                    // pre-allocating the column keeps the size column straight across rows (Layout Stability).
                    style={{ fontSize: 11, fontWeight: 500, color: chipColor, border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm, padding: '1px 8px', flexShrink: 0, width: 168, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >{chipText}</span>
                </div>
              );
            })}
            {/* Muted collapse line for the images the tenant chose not to count/list — only when
                settled (the skeleton branch above owns the loading window) and only while inline
                images actually exist; every word comes from inlineSummaryLabel so this line and the
                per-row OCR chip above can never disagree about the German wording. */}
            {!cm.configLoading && !attachmentsCountInline && anhaengeInline.length > 0 && (
              <div
                data-testid="anhang-inline-summary"
                style={{ fontSize: 11, color: theme.colors.textSecondary, padding: `${theme.spacing.xs}px 0` }}
              >{inlineSummaryLabel(anhaengeInline.length)}</div>
            )}
            {extractionSummary != null && (
              <div style={{ marginTop: theme.spacing.xs }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 3 }}>Zusammenfassung</div>
                <div
                  data-testid="anhang-summary"
                  style={{ padding: theme.spacing.md, backgroundColor: theme.colors.bgPage, borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`, whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid }}
                  dangerouslySetInnerHTML={{ __html: styleRedactionTokens(escapeHtml(extractionSummary)) }}
                />
              </div>
            )}
          </div>
        </PanelSection>

        {/* Email Body Preview */}
        {ticket.emailBodyPreview && (
          <PanelSection title="E-Mail-Inhalt" collapsible expanded={emailExpanded} onToggle={() => setEmailExpanded(p => !p)}>
            {isHtml(ticket.emailBodyPreview) ? (
              <div
                style={{
                  padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
                  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
                  fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
                }}
                dangerouslySetInnerHTML={{
                  __html: styleRedactionTokens(DOMPurify.sanitize(ticket.emailBodyPreview, {
                    FORBID_TAGS: ['style', 'link', 'meta', 'head', 'title', 'script', 'img'],
                    FORBID_ATTR: ['style', 'class'],
                  })),
                }}
              />
            ) : (
              <div
                style={{
                  padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
                  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
                  whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
                }}
                dangerouslySetInnerHTML={{
                  __html: styleRedactionTokens(escapeHtmlText(ticket.emailBodyPreview)),
                }}
              />
            )}
          </PanelSection>
        )}

        {/* Personal Data — per-entry pills with dismiss + optional "always ignore" */}
        {ticket.personalData && Object.keys(ticket.personalData).length > 0 && (() => {
          // Why: dates aren't PII (GDPR) — letter/appointment dates don't identify a person
          const PII_SKIP_FIELDS = new Set(['dates']);
          const groups = Object.entries(ticket.personalData)
            .filter(([key]) => !PII_SKIP_FIELDS.has(normalizeKey(key)))
            .map(([key, value]) => {
              const nk = normalizeKey(key);
              const label = PERSONAL_DATA_LABELS[nk] || key;
              const entries = (Array.isArray(value) ? value as string[] : [String(value)])
                .filter(v => !dismissedPii.has(`${key}:${v}`))
                .filter(v => !piiIgnoreList?.some(e => e.fieldType === nk && e.value === v));
              return { key, nk, label, entries };
            })
            .filter(g => g.entries.length > 0);

          return groups.length > 0 ? (
            <PanelSection title="Erkannte Personendaten">
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, minHeight: 28 }}>
                {groups.map(({ key, nk, label, entries }) => (
                  <div key={key}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 3 }}>{label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {entries.map(v => (
                        <PiiPill
                          key={`${key}:${v}`}
                          value={v}
                          onDismiss={() => setDismissedPii(prev => new Set(prev).add(`${key}:${v}`))}
                          onAlwaysIgnore={onPiiIgnore ? () => onPiiIgnore(nk, v) : undefined}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </PanelSection>
          ) : null;
        })()}

        {/* Metadata */}
        <PanelSection title="Details">
          {/* Addressable because its GEOMETRY is a guarantee, not an implementation detail: the row
              order and the cell count are what item 5's toggle must not disturb, and a test can only
              assert that by looking at this element's children. */}
          <div data-testid="ticket-detail-meta-grid"
               style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, fontSize: 13 }}>
            <MetaItem label="UUID" value={ticket.uuid} mono copyable />
            {/* mig 072 — the MAIL FILE's own name, beside the UUID because that is where the operator
                asked for it. ⚠ NOT the same fact as the Formular-Herkunft tooltip further up: that one
                names an ATTACHMENT INSIDE the mail (mig 069), this one names the file that was dropped.
                ⚠ RENDERED ALWAYS, em-dash when absent — never conditionally mounted. Every pre-072
                ticket is permanently NULL here (unbackfillable: ingestion discarded the value), so a
                conditional row would appear and disappear between tickets and shift this grid on every
                selection. The two conditional siblings below (Vorheriger Status, Archiviert am) are the
                outliers, not this row — do not "harmonise" it toward them. */}
            <MetaItem label="Quelldatei" value={ticket.sourceMailFileName || '—'} truncate copyable />
            <MetaItem label="Status" value={STATUS_LABELS[ticket.status] || ticket.status} />
            {ticket.previousStatus && (
              <MetaItem label="Vorheriger Status" value={STATUS_LABELS[ticket.previousStatus] || ticket.previousStatus} />
            )}
            {ticket.archivedAt && (
              <MetaItem label="Archiviert am" value={formatDate(ticket.archivedAt)} />
            )}
            <MetaItem label="Erstellt" value={formatDate(ticket.createdAt)} />
            <MetaItem label="Aktualisiert" value={formatDate(ticket.updatedAt)} />
            <MetaItem label="Entwurf" value={ticket.hasDraft ? 'Ja' : 'Nein'} />
            {/* mig 072 - a SEPARATE fact from Quelldatei (the file's NAME vs the ROUTE it arrived by);
                the 069 chapter warns against collapsing two facts onto one label. Shown only when the
                tenant asks for it (UI_SHOW_SOURCE_CHANNEL) — the column exists mainly for the coming
                direct mail integration, and the row was rejected as a default at operator E2E.
                ⚠ LAST CELL, AND THAT POSITION IS THE DESIGN, not tidiness. The rule against layout
                shift guards what a user is LOOKING AT; toggling this setting happens on another page,
                so that shift is nobody's problem. But the flag also arrives ASYNCHRONOUSLY from
                /config, which CAN land while a detail is already on screen (a pop-out renders its
                ticket immediately). Mounted mid-grid, that late arrival pushes every following row
                down — measured. Mounted last, it has nothing to push. The earlier placeholder-cell
                approach solved the same problem by leaving a visible gap, which is what the operator
                rejected.
                ⚠ Do NOT move this row up beside Quelldatei for tidiness — the guard in
                TicketDetail.eingangswegToggle.test.tsx exists to catch exactly that. */}
            {showSourceChannel && <MetaItem label="Eingangsweg" value={sourceChannelLabel(ticket.sourceChannel)} />}
          </div>
        </PanelSection>
      </div>

      {/* Actions — 2-slot bar, crossfades on status change.
         Slot 1: Genehmigen → Entwurf herunterladen (or Erneut verarbeiten / Zur Verarbeitung)
         Slot 2: Neu analysieren → Archivieren
         Draft download gated behind approval — CS rep must approve first. */}
      <div style={{
        paddingTop: theme.spacing.md, minHeight: 48,
        borderTop: `1px solid ${theme.colors.borderLight}`, marginTop: theme.spacing.md,
      }}>
        <div style={{
          display: 'flex', gap: theme.spacing.sm,
          opacity: btnFade === 'in' ? 1 : 0, transition: 'opacity 0.15s ease',
        }}>
          {/* Slot 1 */}
          {ticket.status === 'suggestion_ready' && !isTriage && (
            <ActionButton label={actionLoading ? '...' : 'Genehmigen'} onClick={onApprove} disabled={!canApprove || actionLoading || maintenance} title={noDraft ? noDraftTooltip : undefined} bg={theme.colors.navy} icon={!actionLoading ? <CheckIcon size={14} color={theme.colors.white} /> : undefined} />
          )}
          {scaffold && !draft.allConfirmed && ticket.status === 'suggestion_ready' && (
            <span style={{ fontSize: 12, color: theme.colors.textSecondary, alignSelf: 'center' }}>
              Noch {draft.openCount} {draft.openCount === 1 ? 'Feld' : 'Felder'} offen
            </span>
          )}
          {canReprocess && (
            <ActionButton label={actionLoading ? '...' : 'Erneut verarbeiten'} onClick={onReprocess!} disabled={actionLoading || maintenance} bg={theme.colors.error} />
          )}
          {/* Why: draft download only after approval — CS rep must approve before downloading.
              Slot-1 alternative buttons only show when NOT in suggestion_ready — avoids showing
              these when scaffold tickets are gated (canApprove=false but status=suggestion_ready). */}
          {ticket.status !== 'suggestion_ready' && !canReprocess && ticket.hasDraft && ['approved', 'sent'].includes(ticket.status) && (
            /* noDraft: the Step-9 blob exists but is an EMPTY .eml — serving it is the footgun. */
            <ActionButton label="Entwurf herunterladen" onClick={onDownload} disabled={noDraft} title={noDraft ? noDraftTooltip : undefined} bg={theme.colors.navy} />
          )}
          {onGoToUploads && ticket.status !== 'suggestion_ready' && !canReprocess && !ticket.hasDraft && (
            <ActionButton label="Zur Verarbeitung" onClick={onGoToUploads} bg={theme.colors.navy} />
          )}
          {/* Slot 2 */}
          {canReject && (
            <ActionButton label="Neu analysieren" onClick={onReject} disabled={actionLoading || maintenance} outlined icon={<CrossIcon size={14} color={theme.colors.navy} />} />
          )}
          {!canReject && canArchive && (
            <ActionButton label={actionLoading ? '...' : 'Archivieren'} onClick={onArchive!} disabled={actionLoading || maintenance} outlined />
          )}
          {canRestore && (
            <ActionButton
              label={actionLoading ? '...' : 'Wiederherstellen'}
              onClick={onRestore!}
              disabled={actionLoading || maintenance}
              bg={theme.colors.navy}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

// mig 072. The RAW vocabulary token never reaches a clerk — same model/render split as STATUS_LABELS
// above: the server SELECTS from a closed vocabulary, deterministic code RENDERS the German.
// ⚠ THE FALLBACK IS THE EM-DASH, NOT THE TOKEN. The sibling status pattern echoes its raw value when
// unmapped, which suits a vocabulary that grows server-side; this one must not. Undeclared, pre-072 and
// unrecognised-claim all mean the same thing to a clerk - nobody said - and echoing "upload" back as a
// label would present a rejected claim as a fact. Server-side normalization already makes an unknown
// token unreachable; this keeps it unreachable if that ever regresses.
const CHANNEL_LABELS: Record<string, string> = {
  upload: 'Lokal hochgeladen',
  forward: 'Aus Outlook weitergeleitet',
};
const sourceChannelLabel = (channel?: string): string =>
  (channel && CHANNEL_LABELS[channel]) || '—';

// `truncate` keeps a long value on ONE line with an ellipsis and puts the full text in the native
// title tooltip. Added for the Quelldatei row (mig 072): a mail filename can be arbitrarily long, and
// this grid is a fixed two-column layout, so an untruncated name would reflow the whole Details block
// — the layout-shift the always-rendered row exists to avoid in the first place.
// Copy-to-clipboard badge beside a MetaItem label (operator request 2026-08-31: UUID + Quelldatei
// are the two values clerks re-type elsewhere). ALWAYS mounted on a copyable row — never appears on
// hover or success, so the label row's geometry is constant (layout-stability rule). Feedback is an
// in-place icon swap to a checkmark for 1.5s, same cadence as the Kopieren button above. Disabled
// (not hidden) when the value is the absent em-dash, so pre-072 tickets keep the same geometry.
const MetaCopyBadge: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  const disabled = !value || value === '—';
  return (
    <button
      type="button"
      data-testid={`metacopy-${label}`}
      aria-label={`${label} in die Zwischenablage kopieren`}
      title={disabled ? undefined : 'In die Zwischenablage kopieren'}
      disabled={disabled}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => { /* permission denial: stay silent, value remains selectable */ });
      }}
      style={{
        width: 16, height: 16, padding: 0, marginLeft: 4, border: 'none', background: 'none',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 0.65,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        verticalAlign: 'middle', color: copied ? theme.colors.success : theme.colors.textSecondary,
      }}
    >
      {copied ? (
        <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M2 6.5 5 9.5 10 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <rect x="3.5" y="3.5" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M8.5 1.5h-6a1 1 0 0 0-1 1v6" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
};

const MetaItem: React.FC<{ label: string; value: string; mono?: boolean; truncate?: boolean; copyable?: boolean }> = ({ label, value, mono, truncate, copyable }) => (
  // The CELL carries its own testid beside the value's: a layout guard needs the grid child, and
  // climbing from the value element to its parent is exactly the brittle traversal that breaks on
  // any wrapper change.
  <div data-testid={`metacell-${label}`} style={truncate ? { minWidth: 0 } : undefined}>
    <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginBottom: 2 }}>
      {label}
      {copyable && <MetaCopyBadge label={label} value={value} />}
    </div>
    <div
      // Addressable by label so a test can assert on ONE meta row's value without walking the DOM —
      // these values carry no role or accessible name of their own, and the alternative is
      // parent/sibling traversal that breaks on any wrapper change.
      data-testid={`meta-${label}`}
      // Only when truncating: the full value must stay reachable, and a title on every MetaItem would
      // put a redundant tooltip on values that are already fully visible.
      title={truncate ? value : undefined}
      style={{
        fontSize: 13, color: theme.colors.navyMid, fontWeight: 500,
        fontFamily: mono ? 'monospace' : theme.font.family,
        wordBreak: mono ? 'break-all' : undefined,
        ...(truncate ? { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      }}
    >
      {value}
    </div>
  </div>
);

const ActionButton: React.FC<{
  label: string; onClick: () => void; disabled?: boolean; bg?: string; outlined?: boolean;
  icon?: React.ReactNode; title?: string;
}> = ({ label, onClick, disabled, bg, outlined, icon, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
      fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
      color: outlined ? theme.colors.navy : theme.colors.white,
      backgroundColor: outlined ? theme.colors.white : bg || theme.colors.navy,
      border: outlined ? `2px solid ${theme.colors.navy}` : 'none',
      borderRadius: theme.borderRadius.md,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'opacity 0.15s ease, background-color 0.15s ease',
    }}
  >
    {icon}{label}
  </button>
);

const PiiPill: React.FC<{
  value: string;
  onDismiss: () => void; onAlwaysIgnore?: () => Promise<void>;
}> = ({ value, onDismiss, onAlwaysIgnore }) => {
  const [confirming, setConfirming] = useState(false);

  const pillStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    backgroundColor: confirming ? theme.colors.white : theme.colors.bgLight,
    border: `1px solid ${confirming ? theme.colors.navyMuted : theme.colors.borderLight}`,
    borderRadius: theme.borderRadius.sm, fontSize: 12,
    animation: 'stx-fadeIn 0.2s ease',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  };

  if (confirming) {
    return (
      <span style={pillStyle}>
        <span style={{ color: theme.colors.navyMid, fontWeight: 500 }}>Immer ignorieren?</span>
        <button
          onClick={() => { onAlwaysIgnore?.().then(onDismiss); }}
          style={{
            padding: '1px 6px', fontSize: 11, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.white, backgroundColor: theme.colors.navy,
            border: 'none', borderRadius: theme.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Ja
        </button>
        <button
          onClick={() => { setConfirming(false); onDismiss(); }}
          style={{
            padding: '1px 6px', fontSize: 11, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.navyMid, backgroundColor: 'transparent',
            border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Nein
        </button>
      </span>
    );
  }

  return (
    <span style={pillStyle}>
      <span style={{ color: theme.colors.navyMid }}>{value}</span>
      <span
        role="button"
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
          if (onAlwaysIgnore) setConfirming(true);
          else onDismiss();
        }}
        onKeyDown={e => { if (e.key === 'Enter') { if (onAlwaysIgnore) setConfirming(true); else onDismiss(); } }}
        style={{
          cursor: 'pointer', color: theme.colors.textSecondary,
          fontSize: 14, lineHeight: 1, marginLeft: 2,
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={e => { (e.currentTarget).style.color = theme.colors.error; }}
        onMouseLeave={e => { (e.currentTarget).style.color = theme.colors.textSecondary; }}
      >
        &times;
      </span>
    </span>
  );
};

// SuggestionEditor — edit mode for KI-Vorschlag. Chrome (prefix + suffix) read-only;
// the inner Antwortvorschlag region (between data-edit-start/end markers) is contentEditable.
// Why useEffect+ref: dangerouslySetInnerHTML reflows on every render and would wipe the user's
// typing. Set innerHTML once on mount and let the browser own the edit buffer.
const SuggestionEditor: React.FC<{
  split: { prefix: string; inner: string; suffix: string };
  saving: boolean;
  error: string | null;
  editableRef: React.RefObject<HTMLDivElement | null>;
  baseStyle: React.CSSProperties;
  onSave: () => void;
  onCancel: () => void;
}> = ({ split, saving, error, editableRef, baseStyle, onSave, onCancel }) => {
  useEffect(() => {
    if (editableRef.current) {
      editableRef.current.innerHTML = styleRedactionTokens(DOMPurify.sanitize(split.inner));
    }
  }, [editableRef, split.inner]);

  const prefixHtml = styleRedactionTokens(DOMPurify.sanitize(split.prefix));
  const suffixHtml = styleRedactionTokens(DOMPurify.sanitize(split.suffix));
  // De-preambled suggestions have chrome-only prefixes (doctype/html/body) that sanitize to
  // whitespace; a padded empty box above the editor is a visible defect, so mount only real chrome.
  const hasPrefix = prefixHtml.replace(/<[^>]*>/g, '').trim().length > 0;
  const hasSuffix = suffixHtml.replace(/<[^>]*>/g, '').trim().length > 0;

  return (
    <div>
      <div style={{ ...baseStyle, border: `2px solid ${theme.colors.warning}`, padding: 0 }}>
        {hasPrefix && (
          <div
            data-testid="suggestion-chrome-prefix"
            style={{ padding: theme.spacing.md, paddingBottom: 4 }}
            dangerouslySetInnerHTML={{ __html: prefixHtml }}
          />
        )}
        <div
          ref={editableRef}
          data-testid="suggestion-editable"
          contentEditable
          suppressContentEditableWarning
          style={{
            // Explicit longhand on all four sides — a shorthand `margin` mixed with `marginTop`/
            // `marginBottom` longhand only resolves correctly by relying on style-object key order.
            marginLeft: theme.spacing.md,
            marginRight: theme.spacing.md,
            marginTop: hasPrefix ? 0 : theme.spacing.md,
            marginBottom: hasSuffix ? 0 : theme.spacing.md,
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.white,
            border: `1px dashed ${theme.colors.warning}`,
            borderRadius: theme.borderRadius.sm,
            minHeight: 80,
            outline: 'none',
          }}
        />
        {hasSuffix && (
          <div
            data-testid="suggestion-chrome-suffix"
            style={{ padding: theme.spacing.md, paddingTop: 4 }}
            dangerouslySetInnerHTML={{ __html: suffixHtml }}
          />
        )}
      </div>
      {error && (
        <div style={{
          fontSize: 12, padding: '6px 12px', marginTop: theme.spacing.sm,
          borderRadius: theme.borderRadius.sm,
          color: theme.colors.error, backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
        }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 500, fontFamily: theme.font.family,
            color: theme.colors.navyMid, backgroundColor: 'transparent',
            border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1,
          }}
        >Abbrechen</button>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.white, backgroundColor: theme.colors.navy,
            border: 'none', borderRadius: theme.borderRadius.sm,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >{saving ? 'Speichern...' : 'Bearbeitung speichern'}</button>
      </div>
    </div>
  );
};

export default TicketDetailComponent;
